/**
 * workflowContinuationEngine.js
 * Detects natural workflow transitions and incomplete pastoral chains.
 *
 * Pastoral processes have natural progressions:
 *   prayer request → visit follow-up
 *   visit          → Bible study / baptism preparation
 *   family crisis  → restoration workflow
 *   inactive       → recovery workflow
 *
 * Detects when the NEXT step in a chain is missing or hasn't been started.
 * Surfaces this as a gentle suggestion, not a mandate.
 */

import { getActiveWorkflows }  from "../kevia-workflows/workflowState";
import { WORKFLOW_TYPES, WORKFLOW_STATUS, STEP_STATUS } from "../kevia-workflows/workflowSchemas";

// ── Natural workflow transition map ──────────────────────────────────────────
// Each entry: from workflow type → natural next type when from is near completion
const NATURAL_TRANSITIONS = {
  [WORKFLOW_TYPES.PRAYER_FOLLOWUP]:          WORKFLOW_TYPES.FAMILY_FOLLOWUP,
  [WORKFLOW_TYPES.FAMILY_FOLLOWUP]:          WORKFLOW_TYPES.BAPTISM_PREPARATION,
  [WORKFLOW_TYPES.VISIT_CYCLE]:              WORKFLOW_TYPES.BAPTISM_PREPARATION,
  [WORKFLOW_TYPES.INACTIVE_MEMBER_RECOVERY]: WORKFLOW_TYPES.FAMILY_FOLLOWUP,
};

const CHAIN_LABELS = {
  [`${WORKFLOW_TYPES.PRAYER_FOLLOWUP}->${WORKFLOW_TYPES.FAMILY_FOLLOWUP}`]:
    "Después de la oración registrada, sería bueno agendar una visita.",
  [`${WORKFLOW_TYPES.FAMILY_FOLLOWUP}->${WORKFLOW_TYPES.BAPTISM_PREPARATION}`]:
    "El seguimiento familiar está avanzando — podría ser momento para explorar estudios bíblicos.",
  [`${WORKFLOW_TYPES.VISIT_CYCLE}->${WORKFLOW_TYPES.BAPTISM_PREPARATION}`]:
    "Las visitas están en curso — hay señales para avanzar hacia un proceso de discipulado.",
  [`${WORKFLOW_TYPES.INACTIVE_MEMBER_RECOVERY}->${WORKFLOW_TYPES.FAMILY_FOLLOWUP}`]:
    "El proceso de recuperación tiene avance. Seguimiento familiar como próximo paso.",
};

const NEAR_COMPLETE_THRESHOLD = 0.65;

/**
 * @typedef {{
 *   fromType:   string,
 *   toType:     string,
 *   entityName: string | null,
 *   label:      string,
 * }} WorkflowChain
 */

/**
 * Detect workflow chains where the natural next step hasn't been started.
 *
 * @returns {{ chains: WorkflowChain[], compact: string | null }}
 */
export function detectWorkflowChains() {
  const activeWfs   = getActiveWorkflows();
  const activeTypes = new Set(activeWfs.map((w) => w.type));
  const chains      = [];

  for (const wf of activeWfs) {
    const nextType = NATURAL_TRANSITIONS[wf.type];
    if (!nextType) continue;

    // Only suggest transition when current workflow is near complete
    if (wf.progress < NEAR_COMPLETE_THRESHOLD) continue;

    // Skip if the next type already exists for this entity
    const entityId = wf.related_entities?.[0]?.id;
    const alreadyExists = activeWfs.some(
      (w) => w.type === nextType &&
      (!entityId || w.related_entities?.some((e) => e.id === entityId))
    );
    if (alreadyExists) continue;

    const chunkKey  = `${wf.type}->${nextType}`;
    const label     = CHAIN_LABELS[chunkKey] ?? `Considerar iniciar el siguiente proceso pastoral.`;
    const entityName = wf.related_entities?.[0]?.name ?? null;

    chains.push({ fromType: wf.type, toType: nextType, entityName, label });
  }

  const compact = chains.length > 0 ? chains[0].label : null;
  return { chains, compact };
}
